import { useEffect, useId, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCheck, Sparkles, Tag, Trash2, User, X } from 'lucide-react'
import { toast } from 'sonner'
import { bulkTransactions, type BulkTransactionRequest } from '@/api/transactions'
import { listCategories } from '@/api/categories'
import { listMembers } from '@/api/members'
import { Button } from '@/components/ui/button'
import { toMemberOptions } from '@/lib/transactionDateRangeFilter'
import { HIDE_TAB_BAR_ATTR } from '@/components/layout/navItems'
import { AFTER_SAVE_KEYS, errorText } from '../add/formLogic'
import { bulkResultMessage } from './bulkResult'

type Picker = 'category' | 'member' | 'capital' | 'reconciled' | 'delete' | null

function PickerSheet({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  const titleId = useId()
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 md:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className="flex max-h-[80vh] w-full flex-col rounded-t-[20px] bg-[var(--fl-surface)] pb-[max(16px,env(safe-area-inset-bottom))] md:w-[400px] md:rounded-[20px]"
      >
        <div className="flex items-center justify-between px-4 pt-3">
          <h2 id={titleId} className="font-serif text-lg font-semibold text-[var(--fl-heading)]">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
          >
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto px-2 pb-2">{children}</div>
      </div>
    </div>
  )
}

function Choice({ label, onPick }: { label: string; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="flex min-h-11 w-full items-center rounded-lg px-3 text-left text-[15px] text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]"
    >
      {label}
    </button>
  )
}

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mx-3 mt-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--fl-text-3)]">
      {children}
    </p>
  )
}

/**
 * The bar shown while rows are selected: change category, member, one-off or reconciled for all of
 * them, or delete them. The server skips rows an action doesn't fit and says why; the toast relays that.
 */
export function BulkActionBar({
  selectedIds,
  reconcileIds,
  onDone,
  onCancel,
}: {
  selectedIds: string[]
  /** Every transaction behind the selected rows: both legs of a transfer sit on statements. */
  reconcileIds: string[]
  onDone: () => void
  onCancel: () => void
}) {
  const queryClient = useQueryClient()
  const [picker, setPicker] = useState<Picker>(null)
  const count = selectedIds.length
  // This bar takes the bottom of a phone screen, so the tab bar steps aside while it's up.
  useEffect(() => {
    document.body.setAttribute(HIDE_TAB_BAR_ATTR, '')
    return () => document.body.removeAttribute(HIDE_TAB_BAR_ATTR)
  }, [])
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
    enabled: picker === 'category',
  })
  const { data: membersData } = useQuery({
    queryKey: ['members'],
    queryFn: () => listMembers(),
    enabled: picker === 'member',
  })

  const mutation = useMutation({
    mutationFn: (req: Omit<BulkTransactionRequest, 'ids'>) =>
      bulkTransactions({
        ...req,
        ids: req.action === 'SET_RECONCILED' ? reconcileIds : selectedIds,
      }),
    onSuccess: (res, req) => {
      for (const key of [...AFTER_SAVE_KEYS, 'goals', 'loans'])
        queryClient.invalidateQueries({ queryKey: [key] })
      if (req.action === 'SET_RECONCILED') {
        // Counted by row: the server counts each transfer leg, which isn't what was selected.
        toast.success(`${count} marked ${req.reconciled ? 'reconciled' : 'not reconciled'}`)
      } else {
        const { title, detail } = bulkResultMessage(
          req.action === 'DELETE' ? 'Deleted' : 'Updated',
          res
        )
        toast.success(title, detail ? { description: detail } : undefined)
      }
      setPicker(null)
      onDone()
    },
    onError: (err) => {
      setPicker(null)
      toast.error(errorText(err))
    },
  })

  const apply = (req: Omit<BulkTransactionRequest, 'ids'>) => mutation.mutate(req)
  const categories = categoriesData?.categories ?? []

  const action = (label: string, Icon: typeof Tag, onClick: () => void, danger = false) => (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0 || mutation.isPending}
      className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[11.5px] whitespace-nowrap md:px-2.5 font-medium disabled:opacity-40 md:flex-row md:gap-1.5 md:text-[13px] ${
        danger
          ? 'text-[var(--fl-deep-expense)] hover:bg-white/10'
          : 'text-[var(--fl-deep-fg)] hover:bg-white/10'
      }`}
    >
      <Icon size={16} aria-hidden="true" /> {label}
    </button>
  )

  return (
    <>
      <div
        role="toolbar"
        aria-label="Selected transactions"
        className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-1 bg-[var(--fl-deep)] px-3 pt-2 pb-[max(8px,env(safe-area-inset-bottom))] text-[var(--fl-deep-fg)] shadow-[0_-8px_30px_rgba(0,0,0,0.2)] md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2 md:rounded-2xl md:px-4 md:py-2"
      >
        <p
          aria-live="polite"
          className="mr-auto whitespace-nowrap pr-2 text-sm font-semibold md:mr-3"
        >
          {count}
          {/* A phone needs the room for five actions; the count still reads as "N selected". */}
          <span className="sr-only md:not-sr-only"> selected</span>
        </p>
        {action('Category', Tag, () => setPicker('category'))}
        {action('Member', User, () => setPicker('member'))}
        {action('One-off', Sparkles, () => setPicker('capital'))}
        {action('Reconcile', CheckCheck, () => setPicker('reconciled'))}
        {action('Delete', Trash2, () => setPicker('delete'), true)}
        <button
          type="button"
          aria-label="Cancel selection"
          onClick={onCancel}
          className="grid h-11 w-11 place-items-center rounded-xl text-[var(--fl-deep-fg-2)] hover:bg-white/10"
        >
          <X size={18} />
        </button>
      </div>

      {picker === 'category' && (
        <PickerSheet title={`Category for ${count} selected`} onClose={() => setPicker(null)}>
          {(['EXPENSE', 'INCOME'] as const).map((type) => (
            <div key={type}>
              <GroupLabel>
                {type === 'EXPENSE' ? 'Expense categories' : 'Income categories'}
              </GroupLabel>
              {categories
                .filter((c) => c.type === type)
                .map((c) => (
                  <Choice
                    key={c.id}
                    label={c.name}
                    onPick={() => apply({ action: 'SET_CATEGORY', categoryId: c.id })}
                  />
                ))}
            </div>
          ))}
        </PickerSheet>
      )}

      {picker === 'member' && (
        <PickerSheet title={`Member for ${count} selected`} onClose={() => setPicker(null)}>
          <Choice label="No one" onPick={() => apply({ action: 'SET_MEMBER', memberId: null })} />
          {toMemberOptions(membersData).map((m) => (
            <Choice
              key={m.id}
              label={m.label}
              onPick={() => apply({ action: 'SET_MEMBER', memberId: m.id })}
            />
          ))}
        </PickerSheet>
      )}

      {picker === 'capital' && (
        <PickerSheet title={`One-off for ${count} selected`} onClose={() => setPicker(null)}>
          <Choice
            label="Mark as one-off (kept out of monthly totals)"
            onPick={() => apply({ action: 'SET_CAPITAL', isCapitalEvent: true })}
          />
          <Choice
            label="Not one-off"
            onPick={() => apply({ action: 'SET_CAPITAL', isCapitalEvent: false })}
          />
        </PickerSheet>
      )}

      {picker === 'reconciled' && (
        <PickerSheet title={`Reconciled for ${count} selected`} onClose={() => setPicker(null)}>
          <Choice
            label="Mark reconciled (matches the statement)"
            onPick={() => apply({ action: 'SET_RECONCILED', reconciled: true })}
          />
          <Choice
            label="Mark not reconciled"
            onPick={() => apply({ action: 'SET_RECONCILED', reconciled: false })}
          />
        </PickerSheet>
      )}

      {picker === 'delete' && (
        <PickerSheet
          title={`Delete ${count} transaction${count === 1 ? '' : 's'}?`}
          onClose={() => setPicker(null)}
        >
          <p className="px-3 pt-1 text-sm text-[var(--fl-text-2)]">
            Account balances are restored. Settle-ups and entries made by a loan or goal are left
            alone. This can&rsquo;t be undone.
          </p>
          <div className="flex gap-2.5 px-3 pt-4">
            <Button variant="outline" className="h-11 flex-1" onClick={() => setPicker(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="h-11 flex-1 font-semibold"
              disabled={mutation.isPending}
              onClick={() => apply({ action: 'DELETE' })}
            >
              {mutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </PickerSheet>
      )}
    </>
  )
}
